import axios, { AxiosError, AxiosResponse } from "axios";
import { NetworkError, UnexpectedError } from "./Errors";

/**
 * Data response of a query search.
 */
export type SchoolSearchResult = {
  hits: SchoolSearchItem[];
  total: number;
};

/**
 * Data response format of a single school.
 */
export type SchoolSearchItem = {
  id: string;
  text: string;
  name: string;
  street: string;
  city: string;
  state: string;
  postcode: string;
  orgTypeCode: string;
  countryId: number;
  timestamp: number;
};

/**
 * Optional parameters for the search.
 */
export type SchoolSearchOptions = {
  /** Search by a the given term. */
  term?: string;
  /** Get results from the given index. */
  from?: number;
  /** Limit results to the given size. */
  size?: number;
  /** Filter results by country ID. */
  countryId?: number;
};

/**
 * Query the server for a list of schools.
 *
 * @throws {Error}
 */
export async function searchSchool(
  options?: SchoolSearchOptions
): Promise<SchoolSearchResult> {
  const endpoint = "https://www.purplemash.com/pmrest/spirit/school-search/";

  let response: AxiosResponse<SchoolSearchResult>;

  try {
    response = await axios.get(endpoint, {
      params: options,
    });
  } catch (error) {
    if ((<AxiosError>error).response) {
      const err = <AxiosError>error;
      throw new NetworkError(
        `Error while fetching schools. ${err}`,
        err.response?.status
      );
    }
    throw new UnexpectedError(`Error while fetching schools. ${error}`);
  }

  return response.data;
}

/**
 * Report a school search that didn't produce the desired result to the user.
 *
 * @param query - The query string used for the search.
 */
export async function reportSchoolNotFound(query: string) {
  const endpoint =
    "https://www.purplemash.com/pmrest/spirit/school-search-failed/";

  try {
    await axios.post(
      endpoint,
      { search_term: query },
      { headers: { "Content-Type": "Application/JSON" } }
    );
  } catch (error) {
    if ((<AxiosError>error).response) {
      const err = <AxiosError>error;
      throw new NetworkError(
        "Error while reportig school not found.",
        err.response?.status
      );
    }
    throw new UnexpectedError(
      `Error while reportig school not found. ${error}`
    );
  }
}
